import React, { useState, useEffect } from 'react';
import api from '../services/api';
import InputNombre from '../components/InputNombre';
import './Clients.css';

interface Client {
  id: number;
  nom: string;
  prenom: string;
  telephone: string;
  email: string;
  adresse: string;
  ville: string;
  entreprise: string;
  ifu: string;
  type: string;
  soldeCredit: number;
  plafondCredit: number;
  pointsFidelite: number;
  totalAchats: number;
  nombreAchats: number;
  derniereVisite: string;
  actif: boolean;
  notes: string;
  createdAt: string;
}

interface Stats {
  total: number;
  ordinaires: number;
  revendeurs: number;
  techniciens: number;
  avecCredit: number;
  totalCredit: number;
}

const clientVide = {
  nom: '', prenom: '', telephone: '', email: '',
  adresse: '', ville: 'Ouagadougou', entreprise: '',
  ifu: '', rccm: '', type: 'ordinaire',
  plafondCredit: 0, notes: ''
};

const fmt = (n: number) => Number(n).toLocaleString('fr-FR') + ' FCFA';

const typeBadge = (type: string) => {
  if (type === 'revendeur') return <span className="type-badge revendeur">Revendeur</span>;
  if (type === 'technicien') return <span className="type-badge technicien">Technicien</span>;
  return <span className="type-badge ordinaire">Ordinaire</span>;
};

const niveauFidelite = (points: number) => {
  if (points >= 15000) return { label: '⭐ Or', cls: 'or' };
  if (points >= 5000) return { label: '🥈 Argent', cls: 'argent' };
  return { label: '🥉 Bronze', cls: 'bronze' };
};

export default function Clients() {
  const [clients, setClients] = useState<Client[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [recherche, setRecherche] = useState('');
  const [filtreType, setFiltreType] = useState('tous');
  const [chargement, setChargement] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [clientSelectionne, setClientSelectionne] = useState<Client | null>(null);
  const [clientCreditSel, setClientCreditSel] = useState<Client | null>(null);
  const [typeCreditOp, setTypeCreditOp] = useState<'remboursement' | 'credit'>('remboursement');
  const [montantCredit, setMontantCredit] = useState(0);
  const [enEdition, setEnEdition] = useState<number | null>(null);
  const [form, setForm] = useState(clientVide);
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState('');

  useEffect(() => {
    chargerClients();
    chargerStats();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => chargerClients(), 300);
    return () => clearTimeout(timer);
  }, [recherche]);

  const chargerClients = async () => {
    try {
      const res = await api.get('/clients', {
        params: recherche ? { recherche } : {}
      });
      setClients(res.data);
    } catch (e) {
      setErreur('Impossible de charger les clients');
    } finally {
      setChargement(false);
    }
  };

  const chargerStats = async () => {
    try {
      const res = await api.get('/clients/stats');
      setStats(res.data);
    } catch (e) {}
  };

  const clientsFiltres = clients.filter(c =>
    filtreType === 'tous' || c.type === filtreType
  );

  const ouvrirCreation = () => {
    setEnEdition(null);
    setForm(clientVide);
    setErreur('');
    setShowModal(true);
  };

  const ouvrirEdition = (c: Client) => {
    setEnEdition(c.id);
    setForm({ ...clientVide, ...c });
    setErreur('');
    setShowModal(true);
    setShowDetail(false);
  };

  const ouvrirDetail = (c: Client) => {
    setClientSelectionne(c);
    setShowDetail(true);
  };

  const ouvrirSuppression = (c: Client) => {
    setClientSelectionne(c);
    setShowDeleteModal(true);
    setShowDetail(false);
  };

  const ouvrirGestionCredit = (c: Client, type: 'remboursement' | 'credit') => {
    setClientCreditSel(c);
    setTypeCreditOp(type);
    setMontantCredit(0);
    setErreur('');
    setShowCreditModal(true);
    setShowDetail(false);
  };

  const sauvegarder = async () => {
    if (!form.nom) {
      setErreur('Le nom est obligatoire');
      return;
    }
    try {
      if (enEdition) {
        await api.put(`/clients/${enEdition}`, form);
        setSucces('Client modifié avec succès');
      } else {
        await api.post('/clients', form);
        setSucces('Client créé avec succès');
      }
      setShowModal(false);
      chargerClients();
      chargerStats();
      setTimeout(() => setSucces(''), 3000);
    } catch (e: any) {
      setErreur(e.response?.data?.message || 'Une erreur est survenue');
    }
  };

  const supprimer = async () => {
    if (!clientSelectionne) return;
    try {
      await api.delete(`/clients/${clientSelectionne.id}`);
      setSucces('Client supprimé');
      setShowDeleteModal(false);
      chargerClients();
      chargerStats();
      setTimeout(() => setSucces(''), 3000);
    } catch (e) {
      setErreur('Erreur lors de la suppression');
    }
  };

  const traiterCredit = async () => {
    if (!clientCreditSel || !montantCredit) {
      setErreur('Le montant est obligatoire');
      return;
    }
    try {
      if (typeCreditOp === 'remboursement') {
        await api.post(`/clients/${clientCreditSel.id}/remboursement`, { montant: montantCredit });
        setSucces(`Remboursement de ${montantCredit.toLocaleString('fr-FR')} FCFA enregistré`);
      } else {
        await api.post(`/clients/${clientCreditSel.id}/credit`, { montant: montantCredit });
        setSucces(`Crédit de ${montantCredit.toLocaleString('fr-FR')} FCFA ajouté`);
      }
      setShowCreditModal(false);
      chargerClients();
      chargerStats();
      setTimeout(() => setSucces(''), 3000);
    } catch (e: any) {
      setErreur(e.response?.data?.message || 'Erreur');
    }
  };

  if (chargement) return <div className="page-chargement">Chargement des clients...</div>;

  return (
    <div className="clients-page">
      <div className="page-header">
        <div>
          <h1>Clients</h1>
          <p>Gérez votre portefeuille clients et leur fidélité</p>
        </div>
        <button className="btn-primary" onClick={ouvrirCreation}>
          + Nouveau client
        </button>
      </div>

      {succes && <div className="alert-succes">✅ {succes}</div>}
      {erreur && <div className="alert-erreur">⚠️ {erreur}</div>}

      {stats && (
        <div className="stats-bar">
          <div className="stat-item">
            <span className="stat-val">{stats.total}</span>
            <span className="stat-lbl">Total clients</span>
          </div>
          <div className="stat-item">
            <span className="stat-val" style={{ color: '#3B6D11' }}>{stats.ordinaires}</span>
            <span className="stat-lbl">Ordinaires</span>
          </div>
          <div className="stat-item">
            <span className="stat-val" style={{ color: '#185FA5' }}>{stats.revendeurs}</span>
            <span className="stat-lbl">Revendeurs</span>
          </div>
          <div className="stat-item">
            <span className="stat-val" style={{ color: '#854F0B' }}>{stats.techniciens}</span>
            <span className="stat-lbl">Techniciens</span>
          </div>
          <div className="stat-item">
            <span className="stat-val" style={{ color: '#A32D2D', fontSize: 16 }}>
              {Number(stats.totalCredit).toLocaleString('fr-FR')} F
            </span>
            <span className="stat-lbl">Crédit en cours</span>
          </div>
        </div>
      )}

      <div className="search-bar">
        <input
          value={recherche}
          onChange={e => setRecherche(e.target.value)}
          placeholder="🔍 Rechercher par nom, téléphone ou entreprise..."
        />
        <div className="filtre-type">
          {['tous', 'ordinaire', 'revendeur', 'technicien'].map(t => (
            <button key={t}
              className={filtreType === t ? 'active' : ''}
              onClick={() => setFiltreType(t)}>
              {t === 'tous' ? 'Tous' : t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="clients-table">
        <table>
          <thead>
            <tr>
              <th>Client</th>
              <th>Type</th>
              <th>Téléphone</th>
              <th>Fidélité</th>
              <th>Total achats</th>
              <th>Crédit dû</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {clientsFiltres.map(c => {
              const fidelite = niveauFidelite(c.pointsFidelite);
              return (
                <tr key={c.id} onClick={() => ouvrirDetail(c)} className="tr-clickable">
                  <td>
                    <div className="td-client">
                      <div className="client-avatar" style={{
                        background: c.type === 'revendeur' ? '#E6F1FB' :
                          c.type === 'technicien' ? '#FAEEDA' : '#EAF3DE'
                      }}>
                        {c.prenom?.[0] || c.nom[0]}
                      </div>
                      <div>
                        <div className="client-nom">
                          {c.prenom ? `${c.prenom} ${c.nom}` : c.nom}
                        </div>
                        {c.entreprise && (
                          <div className="client-entreprise">{c.entreprise}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>{typeBadge(c.type)}</td>
                  <td className="td-tel">{c.telephone || '—'}</td>
                  <td>
                    <span className={`fidelite-badge ${fidelite.cls}`}>
                      {fidelite.label}
                    </span>
                    <div className="points-txt">{c.pointsFidelite} pts</div>
                  </td>
                  <td className="td-montant">{fmt(c.totalAchats)}</td>
                  <td className={`td-montant ${Number(c.soldeCredit) > 0 ? 'credit-du' : ''}`}>
                    {Number(c.soldeCredit) > 0 ? (
                      <div>
                        <div>{fmt(c.soldeCredit)}</div>
                        <button
                          className="btn-rembourser"
                          onClick={e => { e.stopPropagation(); ouvrirGestionCredit(c, 'remboursement'); }}>
                          💳 Rembourser
                        </button>
                      </div>
                    ) : '—'}
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <div className="td-actions">
                      <button onClick={() => ouvrirEdition(c)}>✏️</button>
                      <button onClick={() => ouvrirSuppression(c)}>🗑️</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {clientsFiltres.length === 0 && (
          <div className="table-empty">
            {recherche ? 'Aucun client trouvé' : 'Aucun client enregistré'}
          </div>
        )}
      </div>

      {/* MODAL DETAIL */}
      {showDetail && clientSelectionne && (
        <div className="modal-overlay" onClick={() => setShowDetail(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Fiche client</h2>
              <button className="modal-close" onClick={() => setShowDetail(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-header">
                <div className="detail-avatar" style={{
                  background: clientSelectionne.type === 'revendeur' ? '#E6F1FB' :
                    clientSelectionne.type === 'technicien' ? '#FAEEDA' : '#EAF3DE'
                }}>
                  {clientSelectionne.prenom?.[0] || clientSelectionne.nom[0]}
                </div>
                <div>
                  <div className="detail-nom">
                    {clientSelectionne.prenom
                      ? `${clientSelectionne.prenom} ${clientSelectionne.nom}`
                      : clientSelectionne.nom}
                  </div>
                  {clientSelectionne.entreprise && (
                    <div className="detail-entreprise">{clientSelectionne.entreprise}</div>
                  )}
                  <div style={{ marginTop: 6 }}>{typeBadge(clientSelectionne.type)}</div>
                </div>
              </div>

              <div className="detail-stats">
                <div className="detail-stat">
                  <div className="detail-stat-val">{fmt(clientSelectionne.totalAchats)}</div>
                  <div className="detail-stat-lbl">Total achats</div>
                </div>
                <div className="detail-stat">
                  <div className="detail-stat-val">{clientSelectionne.nombreAchats}</div>
                  <div className="detail-stat-lbl">Nb. achats</div>
                </div>
                <div className="detail-stat">
                  <div className="detail-stat-val">{clientSelectionne.pointsFidelite}</div>
                  <div className="detail-stat-lbl">Points fidélité</div>
                </div>
                <div className="detail-stat" style={{
                  color: Number(clientSelectionne.soldeCredit) > 0 ? '#A32D2D' : '#3B6D11'
                }}>
                  <div className="detail-stat-val">{fmt(clientSelectionne.soldeCredit)}</div>
                  <div className="detail-stat-lbl">Crédit dû</div>
                </div>
              </div>

              {Number(clientSelectionne.soldeCredit) > 0 && (
                <div className="credit-alerte">
                  ⚠️ Ce client a un solde crédit de <strong>{fmt(clientSelectionne.soldeCredit)}</strong> en attente de remboursement.
                  <button className="btn-rembourser-lg"
                    onClick={() => ouvrirGestionCredit(clientSelectionne, 'remboursement')}>
                    💳 Enregistrer un remboursement
                  </button>
                </div>
              )}

              <div className="detail-info-grid">
                {clientSelectionne.telephone && (
                  <div className="detail-info-item">
                    <span className="detail-info-lbl">📞 Téléphone</span>
                    <span>{clientSelectionne.telephone}</span>
                  </div>
                )}
                {clientSelectionne.email && (
                  <div className="detail-info-item">
                    <span className="detail-info-lbl">📧 Email</span>
                    <span>{clientSelectionne.email}</span>
                  </div>
                )}
                {clientSelectionne.adresse && (
                  <div className="detail-info-item">
                    <span className="detail-info-lbl">📍 Adresse</span>
                    <span>{clientSelectionne.adresse}, {clientSelectionne.ville}</span>
                  </div>
                )}
                {clientSelectionne.ifu && (
                  <div className="detail-info-item">
                    <span className="detail-info-lbl">🏛️ IFU</span>
                    <span>{clientSelectionne.ifu}</span>
                  </div>
                )}
                {clientSelectionne.derniereVisite && (
                  <div className="detail-info-item">
                    <span className="detail-info-lbl">🕐 Dernière visite</span>
                    <span>{new Date(clientSelectionne.derniereVisite).toLocaleDateString('fr-FR')}</span>
                  </div>
                )}
                <div className="detail-info-item">
                  <span className="detail-info-lbl">📅 Client depuis</span>
                  <span>{new Date(clientSelectionne.createdAt).toLocaleDateString('fr-FR')}</span>
                </div>
              </div>

              {clientSelectionne.notes && (
                <div className="detail-notes">
                  <div className="detail-info-lbl">📝 Notes</div>
                  <p>{clientSelectionne.notes}</p>
                </div>
              )}

              <div className="fidelite-section">
                <div className="fidelite-header">
                  <span>Programme de fidélité</span>
                  <span className={`fidelite-badge ${niveauFidelite(clientSelectionne.pointsFidelite).cls}`}>
                    {niveauFidelite(clientSelectionne.pointsFidelite).label}
                  </span>
                </div>
                <div className="fidelite-bar-wrap">
                  <div className="fidelite-bar" style={{
                    width: `${Math.min(100, (clientSelectionne.pointsFidelite / 15000) * 100)}%`
                  }} />
                </div>
                <div className="fidelite-info">
                  {clientSelectionne.pointsFidelite} / 15 000 points pour atteindre le niveau Or
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-danger-outline"
                onClick={() => ouvrirSuppression(clientSelectionne)}>
                🗑️ Supprimer
              </button>
              <button className="btn-bleu"
                onClick={() => ouvrirGestionCredit(clientSelectionne, 'credit')}>
                + Crédit
              </button>
              <button className="btn-secondary" onClick={() => setShowDetail(false)}>Fermer</button>
              <button className="btn-primary" onClick={() => ouvrirEdition(clientSelectionne)}>
                ✏️ Modifier
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL CRÉATION / EDITION */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{enEdition ? 'Modifier le client' : 'Nouveau client'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {erreur && <div className="alert-erreur">⚠️ {erreur}</div>}

              <div className="form-section">Type de client</div>
              <div className="type-selector">
                {[
                  { val: 'ordinaire', label: '👤 Ordinaire', desc: 'Prix public standard' },
                  { val: 'revendeur', label: '🏪 Revendeur', desc: 'Remise 15%' },
                  { val: 'technicien', label: '🔧 Technicien', desc: 'Remise 22%' },
                ].map(t => (
                  <div key={t.val}
                    className={`type-option ${form.type === t.val ? 'active' : ''}`}
                    onClick={() => setForm(f => ({ ...f, type: t.val }))}>
                    <div className="type-option-label">{t.label}</div>
                    <div className="type-option-desc">{t.desc}</div>
                  </div>
                ))}
              </div>

              <div className="form-section">Informations personnelles</div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Prénom</label>
                  <input value={form.prenom}
                    onChange={e => setForm(f => ({ ...f, prenom: e.target.value }))}
                    placeholder="Ex: Amadou" />
                </div>
                <div className="form-group">
                  <label>Nom <span className="req">*</span></label>
                  <input value={form.nom}
                    onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
                    placeholder="Ex: Traoré" />
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Téléphone</label>
                  <input value={form.telephone}
                    onChange={e => setForm(f => ({ ...f, telephone: e.target.value }))}
                    placeholder="+226 XX XX XX XX" />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="client@email.com" />
                </div>
              </div>

              <div className="form-section">Localisation</div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Adresse</label>
                  <input value={form.adresse}
                    onChange={e => setForm(f => ({ ...f, adresse: e.target.value }))}
                    placeholder="Secteur, quartier..." />
                </div>
                <div className="form-group">
                  <label>Ville</label>
                  <select value={form.ville}
                    onChange={e => setForm(f => ({ ...f, ville: e.target.value }))}>
                    <option>Ouagadougou</option>
                    <option>Bobo-Dioulasso</option>
                    <option>Koudougou</option>
                    <option>Banfora</option>
                    <option>Ouahigouya</option>
                    <option>Kaya</option>
                    <option>Fada N'Gourma</option>
                  </select>
                </div>
              </div>

              {(form.type === 'revendeur' || form.type === 'technicien') && (
                <>
                  <div className="form-section">Informations professionnelles</div>
                  <div className="form-group">
                    <label>Nom de l'entreprise</label>
                    <input value={form.entreprise}
                      onChange={e => setForm(f => ({ ...f, entreprise: e.target.value }))}
                      placeholder="Raison sociale" />
                  </div>
                  <div className="form-row-2">
                    <div className="form-group">
                      <label>IFU</label>
                      <input value={form.ifu}
                        onChange={e => setForm(f => ({ ...f, ifu: e.target.value }))}
                        placeholder="Ex: BF-2023-XXXXXXX" />
                    </div>
                    <div className="form-group">
                      <label>RCCM</label>
                      <input value={form.rccm}
                        onChange={e => setForm(f => ({ ...f, rccm: e.target.value }))}
                        placeholder="Ex: BF-OUA-2023-B-XXXXX" />
                    </div>
                  </div>
                </>
              )}

              <div className="form-section">Crédit</div>
              <div className="form-group">
                <label>Plafond de crédit autorisé (FCFA)</label>
                <InputNombre
                  value={form.plafondCredit}
                  onChange={val => setForm(f => ({ ...f, plafondCredit: val }))}
                  placeholder="0 = pas de crédit"
                />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2}
                  placeholder="Informations complémentaires..."
                  style={{
                    width: '100%', padding: '8px 10px',
                    border: '1px solid #E2E8F0', borderRadius: 7,
                    fontFamily: 'inherit', fontSize: 13
                  }} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
              <button className="btn-primary" onClick={sauvegarder}>
                {enEdition ? 'Enregistrer' : 'Créer le client'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL SUPPRESSION */}
      {showDeleteModal && clientSelectionne && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header danger">
              <h2>⚠️ Supprimer le client</h2>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Voulez-vous supprimer <strong>
                {clientSelectionne.prenom
                  ? `${clientSelectionne.prenom} ${clientSelectionne.nom}`
                  : clientSelectionne.nom}
              </strong> ? Cette action est irréversible.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDeleteModal(false)}>Annuler</button>
              <button className="btn-danger" onClick={supprimer}>Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL GESTION CRÉDIT */}
      {showCreditModal && clientCreditSel && (
        <div className="modal-overlay" onClick={() => setShowCreditModal(false)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {typeCreditOp === 'remboursement' ? '💳 Enregistrer un remboursement' : '+ Ajouter un crédit'}
              </h2>
              <button className="modal-close" onClick={() => setShowCreditModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {erreur && <div className="alert-erreur">⚠️ {erreur}</div>}

              <div className="credit-client-info">
                <div className="cci-nom">
                  {clientCreditSel.prenom} {clientCreditSel.nom}
                </div>
                <div className="cci-solde">
                  Solde crédit actuel :
                  <strong style={{ color: Number(clientCreditSel.soldeCredit) > 0 ? '#A32D2D' : '#3B6D11' }}>
                    {' '}{Number(clientCreditSel.soldeCredit).toLocaleString('fr-FR')} FCFA
                  </strong>
                </div>
              </div>

              <div className="credit-op-toggle">
                <button
                  className={typeCreditOp === 'remboursement' ? 'active' : ''}
                  onClick={() => setTypeCreditOp('remboursement')}>
                  💳 Remboursement
                </button>
                <button
                  className={typeCreditOp === 'credit' ? 'active' : ''}
                  onClick={() => setTypeCreditOp('credit')}>
                  + Ajout crédit
                </button>
              </div>

              <div className="form-group" style={{ marginTop: 14 }}>
                <label>
                  Montant à {typeCreditOp === 'remboursement' ? 'rembourser' : 'créditer'} (FCFA)
                </label>
                <InputNombre
                  value={montantCredit}
                  onChange={val => setMontantCredit(val)}
                  placeholder="0"
                />
              </div>

              {typeCreditOp === 'remboursement' && montantCredit > 0 && (
                <div className="credit-preview remboursement">
                  Solde après remboursement :
                  <strong>
                    {' '}{Math.max(0, Number(clientCreditSel.soldeCredit) - montantCredit).toLocaleString('fr-FR')} FCFA
                  </strong>
                </div>
              )}
              {typeCreditOp === 'credit' && montantCredit > 0 && (
                <div className="credit-preview ajout">
                  Nouveau solde crédit :
                  <strong>
                    {' '}{(Number(clientCreditSel.soldeCredit) + montantCredit).toLocaleString('fr-FR')} FCFA
                  </strong>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowCreditModal(false)}>
                Annuler
              </button>
              <button
                className={typeCreditOp === 'remboursement' ? 'btn-primary' : 'btn-bleu-solid'}
                onClick={traiterCredit}>
                {typeCreditOp === 'remboursement' ? '✓ Confirmer le remboursement' : '+ Ajouter le crédit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}